import React from 'react';

const DynamicBlogImage = ({ 
  post, 
  width = 400, 
  height = 250,
  className = '' 
}) => {
  
  const getTemplateVariation = (post) => {
    const hashString = (post.title + post.id).toLowerCase();
    let hash = 0;
    for (let i = 0; i < hashString.length; i++) {
      const char = hashString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    
    const templateIndex = Math.abs(hash) % 8;
    const colorIndex = Math.abs(hash >> 8) % 6;
    
    return { templateIndex, colorIndex };
  };

  const { templateIndex, colorIndex } = getTemplateVariation(post);

  // Enhanced color schemes with more dramatic gradients
  const colorSchemes = [
    {
      primary: '#0891b2',
      secondary: '#1d4ed8', 
      accent: '#22d3ee',
      brand: '#fbbf24',
      background: 'linear-gradient(135deg, #0c4a6e 0%, #1e40af 30%, #7c3aed 70%, #0891b2 100%)',
      textColor: '#ffffff',
      categoryColor: '#22d3ee'
    },
    {
      primary: '#7c3aed',
      secondary: '#ec4899',
      accent: '#a78bfa',
      brand: '#fbbf24',
      background: 'linear-gradient(135deg, #581c87 0%, #7c3aed 30%, #ec4899 70%, #f97316 100%)',
      textColor: '#ffffff',
      categoryColor: '#a78bfa'
    },
    {
      primary: '#059669',
      secondary: '#0891b2',
      accent: '#34d399',
      brand: '#fbbf24',
      background: 'linear-gradient(135deg, #064e3b 0%, #059669 30%, #0891b2 70%, #1d4ed8 100%)',
      textColor: '#ffffff',
      categoryColor: '#34d399'
    },
    {
      primary: '#dc2626',
      secondary: '#ea580c',
      accent: '#fb7185',
      brand: '#22d3ee',
      background: 'linear-gradient(135deg, #7f1d1d 0%, #dc2626 30%, #ea580c 70%, #f59e0b 100%)',
      textColor: '#ffffff',
      categoryColor: '#fb7185'
    },
    {
      primary: '#1e40af',
      secondary: '#0891b2',
      accent: '#3b82f6',
      brand: '#10b981',
      background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 30%, #0891b2 70%, #059669 100%)',
      textColor: '#ffffff',
      categoryColor: '#3b82f6'
    },
    {
      primary: '#be185d',
      secondary: '#7c3aed',
      accent: '#f472b6',
      brand: '#fbbf24',
      background: 'linear-gradient(135deg, #831843 0%, #be185d 30%, #7c3aed 70%, #1d4ed8 100%)',
      textColor: '#ffffff',
      categoryColor: '#f472b6'
    }
  ];

  const colors = colorSchemes[colorIndex];

  const breakTitle = (title, maxLength = 26) => {
    const words = title.split(' ');
    const lines = [];
    let currentLine = '';
    
    words.forEach(word => {
      if (currentLine.length + word.length > maxLength && currentLine !== '') {
        lines.push(currentLine.trim());
        currentLine = word + ' ';
      } else {
        currentLine += word + ' ';
      }
    });
    
    if (currentLine.trim()) lines.push(currentLine.trim());
    return lines.slice(0, 3);
  };

  const titleLines = breakTitle(post.title);

  const seed = Math.abs(post.title.split('').reduce((a, b) => a + b.charCodeAt(0), 0));
  const random = (min, max, offset = 0) => {
    const rnd = Math.sin(seed + offset) * 10000;
    return min + (rnd - Math.floor(rnd)) * (max - min);
  };

  // More dramatic and visually striking templates
  const renderTemplate = () => {
    const templates = [
      // 0: Flowing Waves with Particles
      () => (
        <>
          {/* Large flowing waves */}
          <path d={`M0,${height-100} Q${width/3},${height-140} ${width*2/3},${height-100} T${width},${height-80} L${width},${height} L0,${height} Z`} 
                fill={colors.accent} opacity="0.4" />
          <path d={`M0,${height-60} Q${width/4},${height-90} ${width/2},${height-60} T${width},${height-40} L${width},${height} L0,${height} Z`} 
                fill={colors.brand} opacity="0.3" />
          
          {/* Floating particles */}
          <circle cx={random(60, 120)} cy={random(40, 80)} r={random(2, 4)} fill={colors.accent} opacity="0.7">
            <animate attributeName="cy" values={`${random(40, 80)};${random(20, 60)};${random(40, 80)}`} dur="4s" repeatCount="indefinite" />
          </circle>
          <circle cx={random(280, 340)} cy={random(60, 100)} r={random(1, 3)} fill={colors.brand} opacity="0.8">
            <animate attributeName="cy" values={`${random(60, 100)};${random(40, 80)};${random(60, 100)}`} dur="3s" repeatCount="indefinite" />
          </circle>
          <circle cx={random(180, 240)} cy={random(30, 70)} r={random(1.5, 3.5)} fill={colors.primary} opacity="0.6">
            <animate attributeName="cy" values={`${random(30, 70)};${random(10, 50)};${random(30, 70)}`} dur="5s" repeatCount="indefinite" />
          </circle>
        </>
      ),

      // 1: Geometric Explosion
      () => (
        <>
          {/* Large geometric shapes radiating from corner */}
          <polygon points={`${width-80},0 ${width},0 ${width},60 ${width-40},80`} fill={colors.primary} opacity="0.5" />
          <polygon points={`${width-120},20 ${width-40},20 ${width-60},80 ${width-100},60`} fill={colors.accent} opacity="0.4" />
          <polygon points={`${width-100},40 ${width-20},40 ${width-40},100 ${width-80},80`} fill={colors.brand} opacity="0.3" />
          
          {/* Bottom accent */}
          <polygon points={`0,${height-80} 120,${height-100} 100,${height} 0,${height}`} fill={colors.accent} opacity="0.5" />
          
          {/* Small scattered elements */}
          <rect x={random(50, 100)} y={random(80, 120)} width={random(8, 15)} height={random(8, 15)} fill={colors.brand} opacity="0.6" transform={`rotate(${random(0, 45)} ${random(50, 100)} ${random(80, 120)})`} />
        </>
      ),

      // 2: Circuit Network
      () => (
        <>
          {/* Network lines */}
          <g opacity="0.5">
            <line x1={random(30, 80)} y1={random(40, 80)} x2={random(180, 250)} y2={random(40, 80)} stroke={colors.accent} strokeWidth="2" />
            <line x1={random(180, 250)} y1={random(40, 80)} x2={random(180, 250)} y2={random(120, 180)} stroke={colors.accent} strokeWidth="2" />
            <line x1={random(180, 250)} y1={random(120, 180)} x2={random(300, 370)} y2={random(120, 180)} stroke={colors.accent} strokeWidth="2" />
          </g>
          
          {/* Network nodes */}
          <circle cx={random(180, 250)} cy={random(40, 80)} r="6" fill={colors.brand} opacity="0.8" />
          <circle cx={random(180, 250)} cy={random(120, 180)} r="8" fill={colors.accent} opacity="0.7" />
          <circle cx={random(300, 370)} cy={random(120, 180)} r="5" fill={colors.primary} opacity="0.9" />
          
          {/* Background mesh */}
          <defs>
            <pattern id={`mesh-${post.id}`} x="0" y="0" width="25" height="25" patternUnits="userSpaceOnUse">
              <circle cx="3" cy="3" r="0.5" fill={colors.accent} opacity="0.3"/>
              <circle cx="22" cy="22" r="0.5" fill={colors.brand} opacity="0.4"/>
            </pattern>
          </defs>
          <rect width={width} height={height} fill={`url(#mesh-${post.id})`} />
          
          {/* Large accent shape */}
          <ellipse cx={random(50, 100)} cy={height - 60} rx="40" ry="25" fill={colors.brand} opacity="0.4" />
        </>
      ),

      // 3: Crystalline Structure
      () => (
        <>
          {/* Crystal facets */}
          <polygon points={`${width-120},0 ${width},0 ${width-60},80 ${width-120},60`} fill={colors.primary} opacity="0.4" />
          <polygon points={`${width-60},80 ${width},0 ${width},60 ${width-40},100`} fill={colors.accent} opacity="0.5" />
          <polygon points={`${width-120},60 ${width-60},80 ${width-40},100 ${width-100},80`} fill={colors.brand} opacity="0.3" />
          
          {/* Bottom crystals */}
          <polygon points={`0,${height-60} 40,${height-80} 80,${height-40} 40,${height}`} fill={colors.accent} opacity="0.4" />
          <polygon points={`40,${height} 80,${height-40} 120,${height-20} 80,${height}`} fill={colors.primary} opacity="0.3" />
          
          {/* Sparkle effects */}
          <g opacity="0.8">
            <polygon points={`${random(200, 280)},${random(80, 120)} ${random(202, 282)},${random(85, 125)} ${random(200, 280)},${random(90, 130)} ${random(198, 278)},${random(85, 125)}`} fill={colors.brand} />
            <polygon points={`${random(100, 160)},${random(50, 90)} ${random(102, 162)},${random(55, 95)} ${random(100, 160)},${random(60, 100)} ${random(98, 158)},${random(55, 95)}`} fill={colors.accent} />
          </g>
        </>
      ),

      // 4: Liquid Flow
      () => (
        <>
          {/* Flowing liquid shapes */}
          <path d={`M${width},0 Q${width-100},60 ${width-60},120 Q${width-80},180 ${width},220 L${width},0 Z`} 
                fill={colors.primary} opacity="0.4" />
          <path d={`M${width-40},0 Q${width-140},80 ${width-100},140 Q${width-120},200 ${width-40},240 L${width-40},0 Z`} 
                fill={colors.accent} opacity="0.5" />
          
          {/* Bottom flow */}
          <path d={`M0,${height-80} Q${width/3},${height-120} ${width*2/3},${height-80} Q${width},${height-40} ${width},${height} L0,${height} Z`} 
                fill={colors.brand} opacity="0.4" />
          
          {/* Bubbles */}
          <circle cx={random(80, 140)} cy={random(100, 160)} r={random(4, 8)} fill={colors.accent} opacity="0.6" />
          <circle cx={random(260, 320)} cy={random(60, 120)} r={random(3, 6)} fill={colors.brand} opacity="0.7" />
          <circle cx={random(150, 210)} cy={random(40, 100)} r={random(2, 5)} fill={colors.primary} opacity="0.5" />
        </>
      ),

      // 5: Energy Burst
      () => (
        <>
          {/* Energy rays */}
          <g opacity="0.4">
            <polygon points={`${width-80},50 ${width-70},30 ${width-30},40 ${width-40},60`} fill={colors.accent} />
            <polygon points={`${width-60},70 ${width-50},50 ${width-10},60 ${width-20},80`} fill={colors.brand} />
            <polygon points={`${width-90},90 ${width-80},70 ${width-40},80 ${width-50},100`} fill={colors.primary} />
          </g>
          
          {/* Central energy source */}
          <circle cx={width-60} cy="80" r="15" fill={colors.brand} opacity="0.6" />
          <circle cx={width-60} cy="80" r="8" fill={colors.accent} opacity="0.8" />
          
          {/* Bottom energy wave */}
          <path d={`M0,${height-50} Q${width/4},${height-90} ${width/2},${height-50} Q${width*3/4},${height-10} ${width},${height-30} L${width},${height} L0,${height} Z`} 
                fill={colors.primary} opacity="0.4" />
          
          {/* Scattered energy particles */}
          <circle cx={random(60, 120)} cy={random(120, 180)} r="2" fill={colors.accent} opacity="0.8" />
          <circle cx={random(180, 240)} cy={random(40, 100)} r="1.5" fill={colors.brand} opacity="0.9" />
        </>
      ),

      // 6: Abstract Topology
      () => (
        <>
          {/* Morphing shapes */}
          <path d={`M${width-100},20 Q${width-50},0 ${width-20},40 Q${width},80 ${width-60},100 Q${width-120},80 ${width-100},20 Z`} 
                fill={colors.primary} opacity="0.4" />
          <path d={`M${width-80},40 Q${width-30},20 ${width},60 Q${width-20},100 ${width-80},90 Q${width-100},60 ${width-80},40 Z`} 
                fill={colors.accent} opacity="0.5" />
          
          {/* Bottom morphing */}
          <path d={`M0,${height-70} Q60,${height-110} 120,${height-70} Q180,${height-30} 240,${height-70} Q300,${height-110} ${width},${height-70} L${width},${height} L0,${height} Z`} 
                fill={colors.brand} opacity="0.4" />
          
          {/* Organic dots */}
          <ellipse cx={random(100, 160)} cy={random(80, 140)} rx={random(3, 6)} ry={random(2, 4)} fill={colors.accent} opacity="0.7" />
          <ellipse cx={random(200, 280)} cy={random(120, 180)} rx={random(2, 5)} ry={random(3, 6)} fill={colors.brand} opacity="0.6" />
        </>
      ),

      // 7: Digital Glitch
      () => (
        <>
          {/* Glitch blocks */}
          <rect x={width-120} y={random(20, 40)} width={random(80, 120)} height={random(8, 15)} fill={colors.primary} opacity="0.6" />
          <rect x={width-100} y={random(45, 65)} width={random(60, 100)} height={random(6, 12)} fill={colors.accent} opacity="0.7" />
          <rect x={width-140} y={random(70, 90)} width={random(100, 140)} height={random(10, 18)} fill={colors.brand} opacity="0.5" />
          
          {/* Glitch lines */}
          <g opacity="0.5">
            <rect x="0" y={random(100, 140)} width={random(150, 250)} height="2" fill={colors.accent} />
            <rect x="0" y={random(160, 200)} width={random(120, 200)} height="1" fill={colors.brand} />
            <rect x="0" y={random(180, 220)} width={random(180, 300)} height="3" fill={colors.primary} />
          </g>
          
          {/* Bottom digital noise */}
          <rect x={random(0, 40)} y={height-60} width={random(80, 120)} height={random(20, 40)} fill={colors.brand} opacity="0.4" />
          <rect x={random(60, 120)} y={height-40} width={random(60, 100)} height={random(15, 30)} fill={colors.accent} opacity="0.5" />
        </>
      )
    ];

    return templates[templateIndex]();
  };

  return (
    <div className={`relative overflow-hidden rounded-xl ${className}`}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="w-full h-full">
        <defs>
          {/* Enhanced gradient with more stops */}
          <linearGradient id={`bg-${post.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={colors.background.match(/#[a-fA-F0-9]{6}/g)?.[0] || colors.primary} />
            <stop offset="25%" stopColor={colors.background.match(/#[a-fA-F0-9]{6}/g)?.[1] || colors.secondary} />
            <stop offset="75%" stopColor={colors.background.match(/#[a-fA-F0-9]{6}/g)?.[2] || colors.accent} />
            <stop offset="100%" stopColor={colors.background.match(/#[a-fA-F0-9]{6}/g)?.[3] || colors.brand} />
          </linearGradient>
          
          {/* Multiple radial overlays for depth */}
          <radialGradient id={`overlay1-${post.id}`} cx="20%" cy="20%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.15)" />
            <stop offset="100%" stopColor="transparent" />
          </radialGradient>
          <radialGradient id={`overlay2-${post.id}`} cx="80%" cy="80%">
            <stop offset="0%" stopColor="rgba(0,0,0,0.1)" />
            <stop offset="100%" stopColor="transparent" />
          </radialGradient>
        </defs>
        
        {/* Base gradient */}
        <rect width={width} height={height} fill={`url(#bg-${post.id})`} />
        
        {/* Render dynamic template */}
        {renderTemplate()}
        
        {/* Overlay effects */}
        <rect width={width} height={height} fill={`url(#overlay1-${post.id})`} />
        <rect width={width} height={height} fill={`url(#overlay2-${post.id})`} />
        
        {/* Simple, elegant category - no chunky badge */}
        <text 
          x="20" 
          y="35" 
          fontSize="11" 
          fontWeight="600" 
          fill={colors.categoryColor}
          fontFamily="system-ui, -apple-system, sans-serif"
          letterSpacing="2px"
          opacity="0.9"
        >
          {post.category.toUpperCase()}
        </text>
        
        {/* Title - larger and more prominent */}
        <g>
          {titleLines.map((line, index) => (
            <text 
              key={index}
              x="20" 
              y={70 + (index * 32)} 
              fontSize="26" 
              fontWeight="900" 
              fill={colors.textColor}
              fontFamily="system-ui, -apple-system, sans-serif"
              style={{ filter: 'drop-shadow(0 3px 6px rgba(0,0,0,0.4))' }}
            >
              {line}
            </text>
          ))}
        </g>
        
        {/* Meta - clean and minimal */}
        <text 
          x="20" 
          y={height - 35} 
          fontSize="11" 
          fill="rgba(255,255,255,0.8)" 
          fontFamily="system-ui, -apple-system, sans-serif"
          fontWeight="500"
        >
          {post.date} • {post.readTime}
        </text>
        
        {/* Subtle GALO branding */}
        <text 
          x="20" 
          y={height - 15} 
          fontSize="10" 
          fontWeight="700" 
          fill={colors.brand} 
          fontFamily="system-ui, -apple-system, sans-serif"
          letterSpacing="1px"
          opacity="0.7"
        >
          GALO INDUSTRIES
        </text>
        
        {/* Minimal corner accent */}
        <line x1={width-30} y1={height-1} x2={width-1} y2={height-1} stroke={colors.brand} strokeWidth="2" opacity="0.8" />
        <line x1={width-1} y1={height-30} x2={width-1} y2={height-1} stroke={colors.brand} strokeWidth="2" opacity="0.8" />
      </svg>
    </div>
  );
};

export default DynamicBlogImage;